// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import GlobalStyles from '@mui/material/GlobalStyles';
import KinLogo from '../components/KinLogo';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { isDarkMode } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password);
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Theme-aware styles
  const getThemeStyles = () => {
    if (isDarkMode) {
      return {
        background:
          'radial-gradient(ellipse at center, rgba(0, 152, 223, 0.2) 0%, rgba(11, 12, 24, 0.9) 100%)',
        paperBackground: 'rgba(18, 20, 40, 0.97)',
        paperBorder: '1px solid rgba(255, 255, 255, 0.04)',
        textColor: 'text.primary',
        labelColor: 'text.secondary',
        inputBackground: '#121428',
        inputBorderColor: 'rgba(255, 255, 255, 0.13)',
        inputHoverBorderColor: '#0098DF',
        inputFocusBorderColor: '#0098DF',
        iconColor: 'text.secondary',
        footerColor: 'rgba(255, 255, 255, 0.5)',
        autofillBackground: '#121428',
        autofillTextColor: 'text.primary',
      };
    } else {
      return {
        background: `linear-gradient(118deg, 
                      transparent 0%, 
                      transparent 15%,
                      rgba(139, 195, 74, 0.18) 15%, 
                      rgba(139, 195, 74, 0.18) 22%, 
                      rgba(33, 30, 113, 0.12) 22%,
                      rgba(33, 30, 113, 0.12) 25%,
                      transparent 25%,
                      transparent 42%, 
                      rgba(139, 195, 74, 0.1) 42%, 
                      rgba(139, 195, 74, 0.1) 43%, 
                      rgba(33, 30, 113, 0.08) 43%,
                      rgba(33, 30, 113, 0.08) 44%,
                      transparent 44%,
                      transparent 67%,
                      rgba(139, 195, 74, 0.2) 67%, 
                      rgba(139, 195, 74, 0.2) 75%, 
                      rgba(33, 30, 113, 0.14) 75%,
                      rgba(33, 30, 113, 0.14) 78%,
                      transparent 78%,
                      transparent 91%,
                      rgba(139, 195, 74, 0.09) 91%,
                      rgba(139, 195, 74, 0.09) 93%,
                      rgba(33, 30, 113, 0.07) 93%,
                      rgba(33, 30, 113, 0.07) 94%,
                      transparent 94%),
                     #ffffff`,
        backgroundSize: '180px 180px, 100% 100%',
        paperBackground: 'rgba(255, 255, 255, 0.95)',
        paperBorder: '1px solid rgba(0, 0, 0, 0.08)',
        textColor: '#2c3e50',
        labelColor: '#5a6c7d',
        inputBackground: '#ffffff',
        inputBorderColor: 'rgba(0, 0, 0, 0.23)',
        inputHoverBorderColor: '#211E71',
        inputFocusBorderColor: '#211E71',
        iconColor: '#5a6c7d',
        footerColor: 'rgba(0, 0, 0, 0.6)',
        autofillBackground: '#ffffff',
        autofillTextColor: '#2c3e50',
      };
    }
  };

  const themeStyles = getThemeStyles();

  return (
    <>
      <GlobalStyles
        styles={{
          'input:-webkit-autofill, input:-webkit-autofill:focus, input:-webkit-autofill:hover, input:-webkit-autofill:active':
            {
              WebkitBoxShadow: `0 0 0 1000px ${themeStyles.autofillBackground} inset !important`,
              boxShadow: `0 0 0 1000px ${themeStyles.autofillBackground} inset !important`,
              WebkitTextFillColor: `${themeStyles.autofillTextColor} !important`,
              color: `${themeStyles.autofillTextColor} !important`,
              background: `${themeStyles.autofillBackground} !important`,
              transition: 'background-color 5000s ease-in-out 0s !important',
            },
          input: {
            background: `${themeStyles.inputBackground} !important`,
          },
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: themeStyles.background,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Paper
            elevation={24}
            sx={{
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              background: themeStyles.paperBackground,
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                : '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: themeStyles.paperBorder,
              minWidth: 350,
              maxWidth: 400,
            }}
          >
            {/* Logo — reverse wordmark on dark (no orbit chrome); partner mark on light */}
            {isDarkMode ? (
              <Box
                sx={{
                  width: '100%',
                  mt: 0.5,
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <KinLogo
                  variant="reverse"
                  height={112}
                  width="100%"
                  maxWidth="100%"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '320px',
                  height: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <img
                  src="/test-partner.jpg"
                  alt="Partner Logo"
                  style={{
                    maxWidth: '300px',
                    maxHeight: '140px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
            )}
            {isDarkMode && (
              <Typography
                component="h1"
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: themeStyles.textColor,
                  mb: 4,
                  textAlign: 'center',
                  fontSize: '1.25rem',
                  letterSpacing: 0.5,
                }}
              >
                Starfleet Management Portal
              </Typography>
            )}

            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Break-glass local login — use when Portal SSO is unavailable.
            </Alert>

            {error && (
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: 'error.main',
                  },
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    color: themeStyles.labelColor,
                    fontWeight: 500,
                    mb: 0.5,
                    fontSize: 15,
                    ml: 0.5,
                  }}
                >
                  Username <span style={{ color: '#e53935' }}>*</span>
                </Typography>
                <OutlinedInput
                  id="username"
                  name="username"
                  data-testid="username-input"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter username"
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: themeStyles.iconColor }} />
                    </InputAdornment>
                  }
                  fullWidth
                  sx={{
                    background: `${themeStyles.inputBackground} !important`,
                    borderRadius: 2,
                    color: themeStyles.textColor,
                    fontWeight: 500,
                    height: 48,
                    '& fieldset': {
                      borderColor: themeStyles.inputBorderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: themeStyles.inputHoverBorderColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: themeStyles.inputFocusBorderColor,
                    },
                  }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    color: themeStyles.labelColor,
                    fontWeight: 500,
                    mb: 0.5,
                    fontSize: 15,
                    ml: 0.5,
                  }}
                >
                  Password <span style={{ color: '#e53935' }}>*</span>
                </Typography>
                <OutlinedInput
                  id="password"
                  name="password"
                  data-testid="password-input"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter password"
                  startAdornment={
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: themeStyles.iconColor }} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        size="small"
                        sx={{ color: themeStyles.iconColor }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  }
                  sx={{
                    background: `${themeStyles.inputBackground} !important`,
                    borderRadius: 2,
                    color: themeStyles.textColor,
                    fontWeight: 500,
                    height: 48,
                    '& fieldset': {
                      borderColor: themeStyles.inputBorderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: themeStyles.inputHoverBorderColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: themeStyles.inputFocusBorderColor,
                    },
                  }}
                />
              </Box>
              <Button
                type="submit"
                data-testid="sign-in-button"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  backgroundColor: isDarkMode ? '#0098DF' : '#6ab04c',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#211E71' : '#5a9e3e',
                  },
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: isDarkMode
                    ? '0 4px 12px rgba(0, 152, 223, 0.3)'
                    : '0 4px 12px rgba(106, 176, 76, 0.3)',
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>
          </Paper>
          <Typography
            variant="body2"
            color={themeStyles.footerColor}
            sx={{ mt: 2, textAlign: 'center' }}
          >
            © {new Date().getFullYear()} Isolated Networks. All
            rights reserved.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;
